import { motion } from "motion/react";
import { safeHref } from "../lib/safeHref";
import { Link2, ArrowUpRight } from "lucide-react";
import { useSignedObjectUrl } from "../lib/useSignedObjectUrl";
import { useLinkPreview } from "../lib/useLinkPreview";
import { ThoughtBody } from "./ThoughtBody";

export interface Thought {
  id: string;
  title?: string;
  body: string;
  source_url?: string;
  source_title?: string;
  connections: number;
  labels: string[];
  created_at: string;
  image_path?: string;
  /** Resolved (signed) URL for rendering. Prefer `image_path` as the source of truth. */
  image_url?: string;
  author?: string;
  edited_at?: string;
  notes?: string;
  in_thread?: boolean;
  /** Social v1 visibility — defaults to private on the DB side; kept on the local
   * Thought so the detail sheet can render the public/private toggle accurately. */
  visibility?: "private" | "public";
  /** Set when this card was borrowed from another user's card. */
  source_card_id?: string;
  /** The public edition the card was borrowed out of, when there was one. */
  source_edition_id?: string;
  // Optional link preview metadata (for link-only cards)
  link_preview?: {
    url: string;
    title: string;
    description?: string;
    image?: string;
    site_name?: string;
  };
}

interface ThoughtCardProps {
  thought: Thought;
  index?: number;
}

/* ─── Helpers ─── */

const URL_REGEX = /^https?:\/\/[^\s]+$/i;

export function isLinkOnly(body: string): boolean {
  return URL_REGEX.test(body.trim());
}

export function extractDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}


/* ─── Link Preview Card ─── */

function LinkPreviewCard({
  thought,
  preview,
  index,
}: {
  thought: Thought;
  preview: NonNullable<Thought["link_preview"]>;
  index: number;
}) {
  const domain = extractDomain(preview.url);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.04, ease: "easeOut" }}
      className="group relative rounded-2xl overflow-hidden bg-card hover:shadow-lg hover:shadow-accent/5 transition-all duration-300 w-full"
    >
      {preview.image && (
        <a href={preview.url} target="_blank" rel="noopener noreferrer" className="block overflow-hidden">
          <img
            src={preview.image}
            alt=""
            className="w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </a>
      )}

      <div className="px-3.5 py-3">
        <div className="flex items-center gap-1.5 mb-1.5">
          <Link2 size={10} className="text-muted-foreground/40 shrink-0" />
          <span className="text-muted-foreground/50 truncate" style={{ fontSize: "0.65rem" }}>
            {preview.site_name || domain}
          </span>
        </div>

        <a href={preview.url} target="_blank" rel="noopener noreferrer" className="block group/link">
          <h3
            className="text-primary group-hover/link:text-accent transition-colors line-clamp-2"
            style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "0.9rem", lineHeight: "1.35" }}
          >
            {preview.title}
          </h3>
        </a>

        {preview.description && (
          <p className="text-foreground/60 line-clamp-2 mt-1" style={{ fontSize: "0.78rem", lineHeight: "1.5" }}>
            {preview.description}
          </p>
        )}

        {(thought.labels?.length ?? 0) > 0 && (
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {(thought.labels ?? []).slice(0, 3).map((tag) => (
              <span key={tag} className="px-1.5 py-0.5 rounded-full bg-accent/8 text-accent/60 shrink-0" style={{ fontSize: "0.62rem" }}>
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

/* ─── Standard Card (photo / titled / compact) ─── */

export function ThoughtCard({ thought, index = 0 }: ThoughtCardProps) {
  const isLink = isLinkOnly(thought.body);
  // Always call hooks unconditionally (Rules of Hooks). Only cards without a
  // stored preview trigger enrichment; the function persists the result.
  const fetched = useLinkPreview(isLink && !thought.link_preview ? thought.id : null);
  const signed = useSignedObjectUrl({ bucket: "thought-images", path: thought.image_path });

  // Prefer stored link_preview, then live fetch, then domain fallback
  const linkPreview = isLink
    ? (thought.link_preview ?? fetched ?? {
        url: thought.body.trim(),
        title: thought.source_title || extractDomain(thought.body.trim()),
        description: undefined,
        site_name: extractDomain(thought.body.trim()),
      })
    : null;

  if (linkPreview) {
    return <LinkPreviewCard thought={thought} preview={linkPreview} index={index} />;
  }

  const imageUrl = signed.status === "ready" ? signed.url : thought.image_url;
  const hasImage = !!imageUrl;
  const hasTitle = !!thought.title;
  const isImageFilenameBody =
    (!!thought.image_path || !!thought.image_url) &&
    (!thought.body ||
      thought.body.trim().length === 0 ||
      /^\[Image:/i.test(thought.body.trim()) ||
      /^[\w,\s-]+\.(jpg|jpeg|png|gif|webp)$/i.test(thought.body.trim()));

  const variant = hasImage ? "photo" : hasTitle ? "titled" : "compact";

  const hasLabels = (thought.labels?.length ?? 0) > 0;
  const hasSource = !!thought.source_url;
  const hasConnections = thought.connections > 0;
  const hasTextContent = !isImageFilenameBody;

  // Picture-heavy: no body text — image dominates, title/metadata on hover overlay
  const isPictureHeavy = variant === "photo" && !hasTextContent;

  if (isPictureHeavy) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: index * 0.04, ease: "easeOut" }}
        className="group relative rounded-2xl overflow-hidden w-full"
        style={{ maxHeight: 280 }}
      >
        {imageUrl && (
          <img
            src={imageUrl}
            alt=""
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        )}
        {/* Hover overlay with title + metadata */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-3">
          {hasTitle && (
            <h3
              className="text-white line-clamp-2 mb-1.5"
              style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "0.88rem", lineHeight: "1.3" }}
            >
              {thought.title}
            </h3>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            {hasConnections && (
              <span className="inline-flex items-center gap-1 text-white/80 shrink-0" style={{ fontSize: "0.7rem" }}>
                <Link2 size={10} /> {thought.connections}
              </span>
            )}
            {(thought.labels ?? []).slice(0, 2).map((tag) => (
              <span key={tag} className="px-1.5 py-0.5 rounded-full bg-white/15 text-white/80 backdrop-blur-sm shrink-0" style={{ fontSize: "0.62rem" }}>
                {tag}
              </span>
            ))}
            {hasSource && (
              <span className="inline-flex items-center gap-1 text-white/70 shrink-0" style={{ fontSize: "0.65rem" }}>
                <ArrowUpRight size={9} /> {thought.source_title || "Source"}
              </span>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  // Cover photo + body text: link-preview style (image on top, text below)
  if (variant === "photo") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: index * 0.04, ease: "easeOut" }}
        className="group relative rounded-2xl overflow-hidden bg-card hover:shadow-lg hover:shadow-accent/5 transition-all duration-300 w-full"
      >
        {imageUrl && (
          <div className="overflow-hidden">
            <img
              src={imageUrl}
              alt=""
              className="w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              style={{ maxHeight: 180 }}
            />
          </div>
        )}
        <div className="px-3.5 py-3">
          {hasTitle && (
            <h3
              className="text-primary line-clamp-2 mb-1"
              style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "0.9rem", lineHeight: "1.35" }}
            >
              {thought.title}
            </h3>
          )}
          <ThoughtBody
            preview
            text={thought.body}
            className="text-foreground/60 line-clamp-2"
            style={{ fontSize: "0.8rem", lineHeight: "1.5" }}
          />
          {(hasSource || hasLabels || hasConnections) && (
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {hasSource && (
                <a
                  href={safeHref(thought.source_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-0.5 text-muted-foreground/50 hover:text-accent transition-colors shrink-0"
                  style={{ fontSize: "0.68rem" }}
                >
                  <ArrowUpRight size={10} />
                  {thought.source_title || "Source"}
                </a>
              )}
              {hasConnections && (
                <span className="inline-flex items-center gap-0.5 text-muted-foreground/50" style={{ fontSize: "0.68rem" }}>
                  <Link2 size={10} /> {thought.connections}
                </span>
              )}
              {(thought.labels ?? []).slice(0, 2).map((tag) => (
                <span key={tag} className="px-1.5 py-0.5 rounded-full bg-accent/8 text-accent/60" style={{ fontSize: "0.62rem" }}>
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    );
  }

  // Text-only variants: titled and compact
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.04, ease: "easeOut" }}
      className="group relative rounded-2xl overflow-hidden bg-card hover:shadow-lg hover:shadow-accent/5 transition-all duration-300 w-full"
      style={{ minHeight: 72 }}
    >
      <div className={variant === "titled" ? "px-4 py-4" : "px-3.5 py-3.5"}>
        {hasTitle && (
          <h3
            className="text-primary mb-1.5 line-clamp-2"
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: "0.95rem",
              lineHeight: "1.35",
            }}
          >
            {thought.title}
          </h3>
        )}

        <ThoughtBody
          preview
          text={thought.body}
          className={`text-foreground/75 ${hasTitle ? "line-clamp-3" : "line-clamp-6"}`}
          style={{
            fontSize: hasTitle ? "0.82rem" : "0.88rem",
            lineHeight: "1.6",
            fontFamily: !hasTitle ? "'Fraunces', Georgia, serif" : undefined,
          }}
        />

        {(hasSource || hasLabels || hasConnections) && (
          <div className="flex items-center gap-1.5 mt-2.5 pt-2.5 border-t border-border/20 flex-wrap">
            {hasSource && (
              <a
                href={safeHref(thought.source_url)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-0.5 text-muted-foreground/50 hover:text-accent transition-colors shrink-0"
                style={{ fontSize: "0.68rem" }}
              >
                <ArrowUpRight size={10} />
                {thought.source_title || "Source"}
              </a>
            )}
            {hasConnections && (
              <span className="inline-flex items-center gap-0.5 text-muted-foreground/50 shrink-0" style={{ fontSize: "0.68rem" }}>
                <Link2 size={10} /> {thought.connections}
              </span>
            )}
            {(thought.labels ?? []).slice(0, 3).map((tag) => (
              <span key={tag} className="px-1.5 py-0.5 rounded-full bg-accent/8 text-accent/60 shrink-0" style={{ fontSize: "0.62rem" }}>
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}